// src/components/ai/copilot/AiDraftContextSummary.js
import React from 'react';
import { buildQuickInputSummaryText, isQuickInputEmpty } from '../../../utils/instructorCopilot';

/**
 * Read-only display of what the instructor typed before generation.
 * Shown inside AiDraftReviewModal so the instructor can compare
 * their original input against the AI-expanded draft.
 *
 * Props:
 *   quickInput   — the normalized QuickInput object
 *   compact      — bool — single-line summary (default false)
 */
function AiDraftContextSummary({ quickInput, compact = false }) {
  if (isQuickInputEmpty(quickInput)) {
    return (
      <p className="text-xs text-slate-400 italic">Generated from client signals only — no coach context added.</p>
    );
  }

  const summaryText = buildQuickInputSummaryText(quickInput);

  if (compact) {
    return (
      <p className="text-xs text-slate-500">
        <span className="font-semibold text-violet-600">Your context:</span>{' '}{summaryText}
      </p>
    );
  }

  return (
    <div className="rounded-2xl bg-violet-50 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-violet-500">Your input</p>
      {quickInput?.notes ? (
        <p className="mt-2 text-sm text-slate-700">{quickInput.notes}</p>
      ) : null}
      <div className="mt-2 flex flex-wrap gap-2">
        {quickInput?.targetSkillId ? (
          <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-700">
            Skill: {quickInput.targetSkillId}
          </span>
        ) : null}
        {(quickInput?.focusTags || []).map((tag) => (
          <span key={tag} className="rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-700">
            {tag}
          </span>
        ))}
        {quickInput?.duration ? (
          <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-700">
            {quickInput.duration} min
          </span>
        ) : null}
        {quickInput?.tone && quickInput.tone !== 'professional' ? (
          <span className="rounded-full bg-violet-100 px-2 py-0.5 text-xs text-violet-700">
            Tone: {quickInput.tone}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export default AiDraftContextSummary;
